/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Platform, View, Modal, ViewStyle, Dimensions } from 'react-native';
import {
  addDays,
  addMonths,
  startOfDay,
  differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns';
import { StyleSheet } from 'react-native-unistyles';

import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import DatePickerMenu from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerMenu';
import DatePickerWithBottomSheet from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerWithBottomSheet';
import DatePickerWithCenterSheet from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerWithCenterSheet';
import {
  DatePickerWithBottomSheetRefs,
  DatePickerWithCenterSheetRefs,
  CalendarPriceObject,
} from '@airasia-phoenix/react-native/widgets/DatePickerView/types';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { useDeviceInfo } from '@airasia-phoenix/react-native/utils';
import SnackbarView, {
  SnackbarDuration,
} from '@airasia-phoenix/react-native/components/SnackbarView';
import { useTranslation } from 'react-i18next';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';

import { ACTIVE_FIELDS, ActiveField, CalendarConfig } from '../../types';
import { formatDateLabel, formatDateRangeLabel } from './utils';
import {
  DEFAULT_STAY_DAYS,
  DEFAULT_ONE_WAY_STAY_DAYS,
  TRIP_TYPES,
  CALENDAR_VIEW_CONFIG,
  MOBILE_WEB_HEADER_HEIGHT,
} from '../../configs/constants';
import MobileWebDatePickerBottomSheet from './MobileWebDatePickerBottomSheet';
import { useListFeatureFlag } from '../../hooks';
import PortalWrapperView from '../../components/PortalWrapperView/PortalWrapperView';

export interface CalendarWidgetViewProps {
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
  fromDateLabel: string;
  toDateLabel: string;
  onConfirm: (checkIn: Date, checkOut: Date | undefined) => void;
  onReset: () => void;
  onError?: (message: string) => void;
  tripType?: (typeof TRIP_TYPES)[keyof typeof TRIP_TYPES];
  config: CalendarConfig;
  /** Optional flight price/availability data per date (from LFC) */
  priceValues?: CalendarPriceObject;
  /** Optional return-leg flight price data per date (from LFC) */
  returnPriceValues?: CalendarPriceObject;
  /**
   * Text rendered in the price slot of dates that have no LFC price.
   * Pass this whenever priceValues is used: without it those cells render no
   * price line at all, so their date number centres differently from priced
   * cells and the grid visibly shifts as LFC responses arrive.
   */
  unavailablePricePlaceHolder?: string;
  /** Callback fired when the visible months on the calendar change */
  onViewableItems?: (viewableItemsDates: Date[][]) => void;
  /**
   * Renders both dates in one field ("Fri, 7 Aug - Mon, 10 Aug") instead of two
   * separate check-in/check-out fields, matching the flights travel-date field.
   * Opt-in, so existing two-field callers are unaffected. fromDateLabel and
   * toDateLabel still label the depart/return fields inside the date picker.
   */
  isCombinedField?: boolean;
  /** Label for the combined field. Falls back to fromDateLabel when omitted. */
  combinedDateLabel?: string;
}

const DATE_RANGE_START_ICON = {
  icon: pd.icon.main.actions.date_range,
  height: 24,
  width: 24,
};

const DEFAULT_FALLBACK_CONFIG = {
  maxLos: 30,
  minDateOffsetDays: 0,
  minReturnDateOffsetDays: 1,
  maxDateOffsetDays: 365,
  defaultDepartureOffsetDays: 1,
  defaultReturnOffsetDays: 2,
  defaultStayDays: DEFAULT_STAY_DAYS,
  defaultOneWayStayDays: DEFAULT_ONE_WAY_STAY_DAYS,
  minDate: undefined,
  maxDate: undefined,
};

const clampMaxDate = (minDate: Date, maxDate: Date) =>
  maxDate < minDate ? minDate : maxDate;

const toValidDate = (value: unknown): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
  }

  return undefined;
};

// Single-column month list at md; fits the narrowest md viewport (768).
const CENTER_SHEET_WIDTH = 360;

const CalendarWidgetView = (props: CalendarWidgetViewProps) => {
  const {
    checkInDate,
    checkOutDate,
    fromDateLabel = 'Check-in date',
    toDateLabel = 'Check-out date',
    onConfirm,
    onReset,
    tripType,
    config = DEFAULT_FALLBACK_CONFIG,
    priceValues,
    returnPriceValues,
    unavailablePricePlaceHolder,
    onViewableItems,
    isCombinedField = false,
    combinedDateLabel,
  } = props;

  const { t } = useTranslation('hotels');

  const {
    maxLos: configMaxLos,
    minDateOffsetDays,
    minReturnDateOffsetDays,
    maxDateOffsetDays,
    minDate: configMinDate,
    maxDate: configMaxDate,
  } = config;

  const maxLos = configMaxLos ?? DEFAULT_FALLBACK_CONFIG.maxLos;

  const datePickerRef = useRef<DatePickerWithBottomSheetRefs>(null);
  const didConfirmRef = useRef(false);
  const today = useMemo(() => startOfDay(new Date()), []);

  const [draftDates, setDraftDates] = useState<(Date | undefined)[]>([
    toValidDate(checkInDate),
    toValidDate(checkOutDate),
  ]);
  const [activeField, setActiveField] = useState<ActiveField | undefined>(
    undefined,
  );

  const lastClickTimeRef = useRef<number>(0);

  const [internalError, setInternalError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [calendarScrollIndex, setCalendarScrollIndex] = useState<number | null>(
    null,
  );
  const [anchorLayout, setAnchorLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const { isWeb, isMobileWidth } = useDeviceInfo();
  const currentBreakpoint = getCurrentBreakpoint();
  const isWebView = isWeb && currentBreakpoint > 1;
  // md only: the anchored two-month dropdown is 754px wide and the content
  // column caps at 734, so md uses the centred sheet instead.
  const isCenterSheetView = isWeb && currentBreakpoint === 2;
  const dateAnchorRef = useRef<View>(null);
  const dropdownRef = useRef<View>(null);
  const datePickerCenterSheetRef = useRef<DatePickerWithCenterSheetRefs>(null);
  const windowHeight = Dimensions.get('window').height;
  const isMobileWebView = isWeb && !isWebView;
  const datePickerBottomSheetHeight =
    windowHeight * 0.8 - (isMobileWebView ? MOBILE_WEB_HEADER_HEIGHT : 0);
  const DatePickerBottomSheetComponent = isMobileWebView
    ? MobileWebDatePickerBottomSheet
    : DatePickerWithBottomSheet;

  const defaultMinDate = useMemo(
    () => configMinDate || addDays(today, minDateOffsetDays),
    [configMinDate, today, minDateOffsetDays],
  );
  const selectionMaxDate = useMemo(
    () =>
      configMaxDate ||
      clampMaxDate(
        defaultMinDate,
        addDays(today, Math.max(maxDateOffsetDays, minDateOffsetDays)),
      ),
    [
      configMaxDate,
      defaultMinDate,
      today,
      maxDateOffsetDays,
      minDateOffsetDays,
    ],
  );
  const defaultMaxDate = useMemo(
    () =>
      // When direct maxDate is provided, use it directly without any cap
      // Otherwise, apply 2-month cap
      configMaxDate
        ? configMaxDate
        : clampMaxDate(selectionMaxDate, addMonths(defaultMinDate, 2)),
    [configMaxDate, defaultMinDate, selectionMaxDate],
  );
  const maximumDisplayDate = useMemo(() => {
    const minimumSecondVisibleMonth = new Date(
      defaultMinDate.getFullYear(),
      defaultMinDate.getMonth() + 1,
      1,
    );

    return defaultMaxDate < minimumSecondVisibleMonth
      ? minimumSecondVisibleMonth
      : defaultMaxDate;
  }, [defaultMinDate, defaultMaxDate]);

  /**
   * Dynamic minimumDate/maximumDate: controls which dates are SELECTABLE (enabled/disabled).
   * - When selecting TO date and FROM is set: min = FROM, max = FROM + 30 days
   * - All other cases: use default min/max dates (all dates enabled)
   * Works on all platforms (desktop web, mobile web, native app).
   */
  const calendarMinDate = useMemo(() => {
    return defaultMinDate;
  }, [defaultMinDate]);

  const calendarMaxDate = useMemo(() => {
    if (
      activeField === ACTIVE_FIELDS.RETURN &&
      !draftDates[1] &&
      draftDates[0]
    ) {
      const boundedFromDate =
        draftDates[0] > selectionMaxDate ? selectionMaxDate : draftDates[0];
      const fromPlusMax = addDays(boundedFromDate, maxLos);
      // Cap at the hard limit so dates beyond the allowed range are never enabled
      const cappedReturnMaxDate =
        fromPlusMax > selectionMaxDate ? selectionMaxDate : fromPlusMax;

      return clampMaxDate(calendarMinDate, cappedReturnMaxDate);
    }
    // When not in RETURN field, use the constrained max date
    // When configMaxDate is provided (flight-constrained), use it directly
    return configMaxDate || selectionMaxDate;
  }, [
    activeField,
    draftDates,
    maxLos,
    selectionMaxDate,
    calendarMinDate,
    configMaxDate,
  ]);

  const getCalendarScrollIndex = useCallback(
    (targetDate: Date | undefined) => {
      const resolvedTargetDate = toValidDate(targetDate);

      if (!resolvedTargetDate || !defaultMinDate) return 0;

      // Use fixed defaultMinDate as base (like flights uses CALENDAR_START_DATE)
      // This ensures index calculation remains consistent regardless of activeField changes
      const yearsDiff =
        resolvedTargetDate.getFullYear() - defaultMinDate.getFullYear();
      const monthsDiff =
        resolvedTargetDate.getMonth() - defaultMinDate.getMonth();
      const index = yearsDiff * 12 + monthsDiff;

      const maxIndex = Math.max(
        0,
        differenceInCalendarMonths(defaultMaxDate, defaultMinDate),
      );

      const finalIndex = Math.max(0, Math.min(index, maxIndex));

      return finalIndex;
    },
    [defaultMinDate, defaultMaxDate],
  );

  const resolvedPriceValues = useMemo(() => {
    if (activeField === ACTIVE_FIELDS.RETURN && returnPriceValues) {
      return returnPriceValues;
    }
    return priceValues ?? [];
  }, [activeField, priceValues, returnPriceValues]);

  const measureAnchor = useCallback(() => {
    if (!dateAnchorRef.current) return;

    const el = dateAnchorRef.current as unknown as HTMLElement;
    if (!el.getBoundingClientRect) return;

    const rect = el.getBoundingClientRect();

    setAnchorLayout({
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  const portalStyle: ViewStyle = useMemo(() => {
    if (!anchorLayout) return { opacity: 0 };

    const { DROPDOWN_WIDTH, EDGE_PADDING, GAP } = CALENDAR_VIEW_CONFIG;

    const desiredLeft =
      anchorLayout.x + anchorLayout.width / 2 - DROPDOWN_WIDTH / 2;

    const left = Math.max(
      EDGE_PADDING,
      Math.min(desiredLeft, window.innerWidth - DROPDOWN_WIDTH - EDGE_PADDING),
    );

    const top = anchorLayout.y + anchorLayout.height + GAP;

    return {
      position: 'absolute',
      left,
      top,
      width: DROPDOWN_WIDTH,
      maxWidth: window.innerWidth - EDGE_PADDING * 2,
      zIndex: 22,
    };
  }, [anchorLayout]);

  const dropDownStyle: ViewStyle = useMemo(() => {
    if (!anchorLayout) return { opacity: 0 };
    const DROPDOWN_WIDTH = 752;
    const EDGE_PADDING = 16;
    const GAP = 54;

    let layoutLeft = anchorLayout.x;
    let layoutWidth = anchorLayout.width;

    // 1. Locate the precise search widget container using its ID
    if (
      typeof document !== 'undefined' &&
      Platform.OS === 'web' &&
      dateAnchorRef.current
    ) {
      const anchorEl = dateAnchorRef.current as unknown as HTMLElement;
      const widgetEl = anchorEl.closest('#search-widget-container');

      if (widgetEl) {
        const rect = widgetEl.getBoundingClientRect();
        layoutLeft = rect.left;
        layoutWidth = rect.width;
      }
    }

    // 2. Ensure we respect a minimum padding from the screen edge
    const safeLayoutLeft = layoutLeft + EDGE_PADDING;
    const safeLayoutRight = layoutLeft + layoutWidth - EDGE_PADDING;

    // 3. Translate the widget boundaries to local coordinates relative to the anchor
    const minLocalLeft = safeLayoutLeft - anchorLayout.x;
    const maxLocalLeft = safeLayoutRight - DROPDOWN_WIDTH - anchorLayout.x;

    // 4. Calculate the ideal centered offset relative to the anchor
    const localDesiredLeft = anchorLayout.width / 2 - DROPDOWN_WIDTH / 2;

    // 5. Clamp the position perfectly
    const left = Math.max(
      minLocalLeft,
      Math.min(localDesiredLeft, maxLocalLeft),
    );
    const top = GAP;

    return {
      position: 'absolute',
      left,
      top,
      width: DROPDOWN_WIDTH,
      maxWidth: window.innerWidth - EDGE_PADDING * 2,
      zIndex: 22,
    };
  }, [anchorLayout]);

  useEffect(() => {
    if (!isWebView || !isOpen) return;

    const handleReposition = () => {
      measureAnchor();
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isWebView, isOpen, measureAnchor]);

  const handleClose = useCallback(() => {
    if (!didConfirmRef.current) {
      setDraftDates([toValidDate(checkInDate), toValidDate(checkOutDate)]);
    }
    didConfirmRef.current = false;
    setInternalError('');
    if (isWebView) {
      setIsOpen(false);
    }
  }, [checkInDate, checkOutDate, isWebView]);

  // The centred sheet is imperative (open/close via ref) unlike the
  // declaratively rendered dropdown, so mirror isOpen onto it.
  useEffect(() => {
    if (!isCenterSheetView || !isWebView) return;
    if (isOpen) {
      datePickerCenterSheetRef.current?.open();
    } else {
      datePickerCenterSheetRef.current?.close();
    }
  }, [isCenterSheetView, isWebView, isOpen]);

  useEffect(() => {
    // The centred sheet is not inside dropdownRef, so every click in it would
    // read as an outside press. It handles its own dismissal.
    if (!isWebView || !isOpen || isCenterSheetView) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;

      const clickedAnchor =
        dateAnchorRef.current &&
        (dateAnchorRef.current as unknown as Node).contains?.(target);

      const clickedDropdown =
        dropdownRef.current &&
        (dropdownRef.current as unknown as Node).contains?.(target);

      if (!clickedAnchor && !clickedDropdown) {
        handleClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isWebView, isOpen, isCenterSheetView, handleClose]);

  useEffect(() => {
    setDraftDates([toValidDate(checkInDate), toValidDate(checkOutDate)]);
  }, [checkInDate, checkOutDate]);

  useEffect(() => {
    if (!isOpen) {
      setCalendarScrollIndex(getCalendarScrollIndex(checkInDate));
    }
    // Only react to confirmed checkInDate prop changes, not internal scroll index recalculations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, isOpen]);

  const openCalendar = useCallback(
    (field: ActiveField) => {
      setActiveField(field);
      setInternalError('');

      // If calendar is already open (web), preserve current draft dates
      // Only reset from confirmed props when freshly opening the calendar
      if (!isOpen) {
        didConfirmRef.current = false;
        const freshDates: (Date | undefined)[] = [
          toValidDate(checkInDate),
          tripType === TRIP_TYPES.ONE_WAY
            ? undefined
            : toValidDate(checkOutDate),
        ];
        setDraftDates(freshDates);
      }

      const targetDate = draftDates[0] ?? checkInDate;
      const nextScrollIndex = getCalendarScrollIndex(targetDate);

      if (isWebView) {
        setCalendarScrollIndex(nextScrollIndex);
        setIsOpen(true);
        // Defer layout measurement to next frame to unblock the UI
        requestAnimationFrame(() => measureAnchor());
      } else {
        setCalendarScrollIndex(nextScrollIndex);
        datePickerRef.current?.toggleDatePicker();
      }
    },
    [
      isWebView,
      isOpen,
      measureAnchor,
      draftDates,
      checkInDate,
      checkOutDate,
      tripType,
      getCalendarScrollIndex,
    ],
  );

  const handleConfirm = useCallback(() => {
    const [start, initialEnd] = draftDates;
    let end = initialEnd;
    const isOneWay = tripType === TRIP_TYPES.ONE_WAY;

    if (!start) {
      setInternalError(
        isOneWay
          ? t('hotelSearchPage.validation.checkInRequired')
          : t('hotelSearchPage.validation.datesRequired'),
      );
      return;
    }

    if (!isOneWay && !end) {
      // If end date is missing for a round trip, auto-select it
      end = addDays(start, DEFAULT_STAY_DAYS);
      setDraftDates([start, end]);
    } else if (isOneWay && !end) {
      // If end date is missing for a one-way trip, auto-select it
      end = addDays(start, DEFAULT_ONE_WAY_STAY_DAYS);
      setDraftDates([start, end]);
    }

    if (start && end && !isOneWay) {
      const dayDiff = differenceInCalendarDays(end, start);
      if (dayDiff < minReturnDateOffsetDays) {
        setInternalError(
          t('hotelSearchPage.calendar.minReturnDateOffsetValidation', {
            minReturnDateOffsetDays,
          }),
        );
        return;
      }
    }

    didConfirmRef.current = true;
    onConfirm(start, end);
    datePickerRef.current?.toggleDatePicker();
  }, [draftDates, tripType, onConfirm, minReturnDateOffsetDays, t]);

  // Commits like a Confirm press, minus the dismiss and the inline errors.
  const persistSelectedDates = useCallback(
    (dates: (Date | undefined)[]) => {
      const [start, end] = dates;
      const isOneWay = tripType === TRIP_TYPES.ONE_WAY;

      // Nothing committed here, so let handleClose fall back to the props.
      const skip = () => {
        didConfirmRef.current = false;
      };

      // Only commit a complete selection; Confirm still fills a missing date.
      if (!start) return skip();
      if (!isOneWay && !end) return skip();

      if (
        !isOneWay &&
        end &&
        differenceInCalendarDays(end, start) < minReturnDateOffsetDays
      ) {
        return skip();
      }

      didConfirmRef.current = true;
      onConfirm(start, isOneWay ? undefined : end);
      // No toggleDatePicker() / setIsOpen(false) - the picker stays open.
    },
    [tripType, onConfirm, minReturnDateOffsetDays],
  );

  /**
   * RESET LOGIC:
   * Clears all dates, resets active field, and scrolls to current month.
   */
  const onResetPress = useCallback(() => {
    const resetScrollIndex = getCalendarScrollIndex(startOfDay(new Date()));

    const clearedDates = [undefined, undefined];
    setDraftDates(clearedDates);

    // Reset active field to DEPART so calendar shows default date range
    setActiveField(ACTIVE_FIELDS.DEPART);

    setCalendarScrollIndex(null);
    setTimeout(() => {
      setCalendarScrollIndex(resetScrollIndex);
    }, 0);

    onReset();
  }, [onReset, getCalendarScrollIndex]);

  const handleDateChange = useCallback(
    (dates: (Date | undefined)[]) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < 150) {
        // Ignore rapid consecutive triggers within 150ms (confused re-renders / double-trigger bubbling)
        return;
      }
      lastClickTimeRef.current = now;

      // Safely parse each date using toValidDate in case the picker returns date strings or objects
      const parsedDates = dates.map((d) => toValidDate(d));

      // Safely sort dates chronologically to ensure index 0 is always check-in (earlier date)
      // and index 1 is always check-out (later date).
      const sortedDates = [...parsedDates].sort((a, b) => {
        if (!a) return 1;
        if (!b) return -1;
        return a.getTime() - b.getTime();
      });

      const currentActiveField = activeField;
      const prevCheckIn = draftDates[0];
      const prevCheckOut = draftDates[1];
      const adjustedDates: (Date | undefined)[] = [...sortedDates];

      // 1. Isolate the exact newly clicked date regardless of array sorting or library behavior
      let clickedDate: Date | undefined;
      const candidate1 = sortedDates[0];
      const candidate2 = sortedDates[1];

      const isCand1New =
        candidate1 &&
        (!prevCheckIn ||
          differenceInCalendarDays(candidate1, prevCheckIn) !== 0) &&
        (!prevCheckOut ||
          differenceInCalendarDays(candidate1, prevCheckOut) !== 0);

      const isCand2New =
        candidate2 &&
        (!prevCheckIn ||
          differenceInCalendarDays(candidate2, prevCheckIn) !== 0) &&
        (!prevCheckOut ||
          differenceInCalendarDays(candidate2, prevCheckOut) !== 0);

      if (isCand1New) {
        clickedDate = candidate1;
      } else if (isCand2New) {
        clickedDate = candidate2;
      } else {
        clickedDate = candidate2 || candidate1;
      }

      if (!clickedDate) return;

      // 2. State Machine with Validation Preservation and Active-Tab Ping-Pong
      const isOneWay = tripType === TRIP_TYPES.ONE_WAY;

      if (isOneWay) {
        /**
         * One-Way Trips:
         * Clicking any date unconditionally updates Check-in and clears Check-out.
         */
        adjustedDates[0] = clickedDate;
        adjustedDates[1] = undefined;
        setActiveField(ACTIVE_FIELDS.RETURN);
      } else {
        if (currentActiveField === ACTIVE_FIELDS.DEPART) {
          /**
           * User is selecting CHECK-IN:
           * - Set Check-in to clicked date.
           * - If existing Check-out is still valid, KEEP IT!
           * - If existing Check-out exceeds maxLos, AUTO-SHIFT IT!
           */
          adjustedDates[0] = clickedDate;
          const existingCheckOut = prevCheckOut;

          if (existingCheckOut) {
            if (clickedDate >= existingCheckOut) {
              adjustedDates[1] = undefined;
            } else {
              const dayDiff = differenceInCalendarDays(
                existingCheckOut,
                clickedDate,
              );
              if (dayDiff > maxLos) {
                adjustedDates[1] = addDays(clickedDate, maxLos); // Auto-shift check-out!
              } else {
                adjustedDates[1] = existingCheckOut; // Keep it!
              }
            }
          } else {
            adjustedDates[1] = addDays(clickedDate, DEFAULT_STAY_DAYS);
          }

          setActiveField(ACTIVE_FIELDS.RETURN);
        } else if (currentActiveField === ACTIVE_FIELDS.RETURN) {
          /**
           * User is selecting CHECK-OUT:
           */
          if (prevCheckIn) {
            const checkInDiff = differenceInCalendarDays(
              clickedDate,
              prevCheckIn,
            );

            if (checkInDiff < 0) {
              /**
               * Clicked date is earlier than previous check-in.
               * Treat this as a check-in selection!
               * - Set Check-in to clicked date.
               * - If previous check-out is still valid, KEEP IT!
               * - If previous check-out exceeds maxLos, AUTO-SHIFT IT!
               */
              adjustedDates[0] = clickedDate;
              const existingCheckOut = prevCheckOut;

              if (existingCheckOut) {
                const dayDiff = differenceInCalendarDays(
                  existingCheckOut,
                  clickedDate,
                );
                if (dayDiff > maxLos) {
                  adjustedDates[1] = addDays(clickedDate, maxLos); // Auto-shift!
                } else {
                  adjustedDates[1] = existingCheckOut; // Keep it!
                }
              } else {
                adjustedDates[1] = undefined;
              }
              // Focus remains on RETURN so user can proceed to pick check-out if they want
            } else if (checkInDiff > maxLos) {
              /**
               * Clicked date is > 30 nights from check-in.
               * Treat this as a brand-new check-in selection!
               * - Set Check-in to clicked date.
               * - Clear Check-out.
               */
              adjustedDates[0] = clickedDate;
              adjustedDates[1] = undefined;
              // Focus remains on RETURN so user can proceed to pick check-out
            } else {
              /**
               * Valid Check-out date selected!
               * - Set Check-out to clicked date.
               * - Keep Check-in.
               * - Focus transition back to Check-in tab (DEPART) so next tap changes check-in!
               */
              adjustedDates[0] = prevCheckIn;
              adjustedDates[1] = clickedDate;
              setActiveField(ACTIVE_FIELDS.DEPART);
            }
          } else {
            // Fallback if check-in was somehow missing
            adjustedDates[0] = clickedDate;
            adjustedDates[1] = undefined;
            setActiveField(ACTIVE_FIELDS.RETURN);
          }
        }
      }

      setDraftDates(adjustedDates);
      persistSelectedDates(adjustedDates);

      if (internalError) setInternalError('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tripType,
      internalError,
      maxLos,
      activeField,
      draftDates,
      persistSelectedDates,
    ],
  );

  const handleDepartDatePress = useCallback(() => {
    openCalendar(ACTIVE_FIELDS.DEPART);
  }, [openCalendar]);

  const handleReturnDatePress = useCallback(() => {
    openCalendar(ACTIVE_FIELDS.RETURN);
  }, [openCalendar]);

  /**
   * The combined field has no per-leg tap target, so it resumes on the leg the
   * user still owes — return when check-in is already picked and check-out is
   * not — mirroring the flights travel-date field.
   */
  const handleCombinedDatePress = useCallback(() => {
    const shouldResumeOnReturn =
      tripType !== TRIP_TYPES.ONE_WAY && !!checkInDate && !checkOutDate;

    openCalendar(
      shouldResumeOnReturn ? ACTIVE_FIELDS.RETURN : ACTIVE_FIELDS.DEPART,
    );
  }, [checkInDate, checkOutDate, openCalendar, tripType]);

  const combinedDateValue = useMemo(
    () =>
      formatDateRangeLabel(
        draftDates[0],
        tripType === TRIP_TYPES.ONE_WAY ? undefined : draftDates[1],
      ),
    [draftDates, tripType],
  );

  const combinedStartIcon = useMemo(
    () => ({
      ...DATE_RANGE_START_ICON,
      tint: getColor(pd.sys.colour.content_secondary),
    }),
    [],
  );

  useEffect(() => {
    if (tripType === TRIP_TYPES.ONE_WAY) {
      setDraftDates((prev) => [prev[0], undefined]);
      setActiveField(ACTIVE_FIELDS.DEPART);
    }
  }, [tripType]);

  const departDateProps = useMemo(
    () => ({
      label: fromDateLabel,
      disabled: false,
      ...(isMobileWebView
        ? {
            editable: false,
            maxLength: 0,
            selection: { start: 0, end: 0 },
            style: { pointerEvents: 'none' as const },
          }
        : {}),
    }),
    [fromDateLabel, isMobileWebView],
  );

  const returnDateProps = useMemo(
    () => ({
      label: toDateLabel,
      disabled: false,
      ...(isMobileWebView
        ? {
            editable: false,
            maxLength: 0,
            selection: { start: 0, end: 0 },
            style: { pointerEvents: 'none' as const },
          }
        : {}),
    }),
    [isMobileWebView, toDateLabel],
  );

  const datePickerProps = useMemo(
    () => ({
      departDateTextFieldViewProps: departDateProps,
      returnDateTextFieldViewProps: returnDateProps,
      minimumDate: calendarMinDate,
      maximumDate: calendarMaxDate,
      minimumDisplayDate: defaultMinDate,
      maximumDisplayDate,
      isSingleSelection: tripType === TRIP_TYPES.ONE_WAY,
      selectedDate: draftDates,
      onChange: handleDateChange,
      // Renders the Sun–Sat header row above the grid, not a night count, so it
      // stays on for one-way too.
      showDaysIndicator: true,
      priceValues: resolvedPriceValues || [],
      unavailablePricePlaceHolder,
      onViewableItems,
      activeField:
        activeField === ACTIVE_FIELDS.RETURN && draftDates[0] && draftDates[1]
          ? ACTIVE_FIELDS.DEPART
          : activeField,
      onActiveFieldChange:
        draftDates[0] && draftDates[1] ? () => {} : setActiveField,
      calendarScrollIndex,
    }),
    [
      departDateProps,
      returnDateProps,
      calendarMinDate,
      calendarMaxDate,
      defaultMinDate,
      maximumDisplayDate,
      tripType,
      draftDates,
      handleDateChange,
      resolvedPriceValues,
      unavailablePricePlaceHolder,
      onViewableItems,
      activeField,
      calendarScrollIndex,
    ],
  );

  const handleCloseIconPress = useCallback(() => {
    handleClose();
    datePickerRef.current?.toggleDatePicker();
  }, [handleClose]);

  const bottomSheetViewProps = useMemo(
    () => ({
      onCloseIconPress: handleCloseIconPress,
      onBottomSheetClose: handleClose,
      children: <></>,
      initialHeight: datePickerBottomSheetHeight,
      finalHeight: datePickerBottomSheetHeight,
    }),
    [handleClose, handleCloseIconPress, datePickerBottomSheetHeight],
  );

  const stickyBottomBarViewProps = useMemo(
    () => ({
      primaryButtonLabel: t('hotelSearchPage.calendar.confirm'),
      secondaryButtonLabel: t('hotelSearchPage.calendar.reset'),
      primaryButtonOnPress: handleConfirm,
      secondaryButtonOnPress: onResetPress,
    }),
    [handleConfirm, onResetPress, t],
  );

  const handlePrimaryButtonPress = useCallback(() => {
    handleConfirm();
    setIsOpen(false);
  }, [handleConfirm]);

  const handleDismissError = useCallback(() => {
    setInternalError('');
  }, []);

  const { hotel_enable_portal_usage: isPortalUsageEnabled } =
    useListFeatureFlag();

  return (
    <View ref={dateAnchorRef} style={styles.inputRow}>
      {isCombinedField ? (
        <View style={styles.flex1}>
          <TextFieldView
            label={combinedDateLabel ?? fromDateLabel}
            value={combinedDateValue}
            editable={false}
            onPress={handleCombinedDatePress}
            startIcon={combinedStartIcon}
            disabled={false}
            highlightBorder={isOpen}
            testID="calendar-combined-date-field"
          />
        </View>
      ) : (
        <>
          <View style={styles.flex1}>
            <TextFieldView
              label={fromDateLabel}
              value={draftDates[0] ? formatDateLabel(draftDates[0]) : ''}
              editable={false}
              onPress={handleDepartDatePress}
              startIcon={DATE_RANGE_START_ICON}
              disabled={false}
              highlightBorder={activeField === ACTIVE_FIELDS.DEPART && isOpen}
            />
          </View>
          {tripType !== TRIP_TYPES.ONE_WAY && (
            <View style={styles.flex1}>
              <TextFieldView
                label={toDateLabel}
                value={draftDates[1] ? formatDateLabel(draftDates[1]) : ''}
                editable={false}
                onPress={handleReturnDatePress}
                startIcon={DATE_RANGE_START_ICON}
                disabled={false}
                highlightBorder={activeField === ACTIVE_FIELDS.RETURN && isOpen}
              />
            </View>
          )}
        </>
      )}

      {isWebView && isCenterSheetView ? (
        <DatePickerWithCenterSheet
          key={isOpen ? 'center-sheet-open' : 'center-sheet-closed'}
          ref={datePickerCenterSheetRef}
          width={CENTER_SHEET_WIDTH}
          datePickerProps={datePickerProps}
          centerSheetViewProps={{
            onCloseIconPress: () => setIsOpen(false),
            onCenterSheetViewClose: handleClose,
          }}
          stickyBottomBarViewProps={{
            ...stickyBottomBarViewProps,
            primaryButtonOnPress: handlePrimaryButtonPress,
          }}
        />
      ) : isOpen && isWebView && anchorLayout ? (
        <PortalWrapperView>
          <View
            ref={dropdownRef}
            nativeID="calendar-dropdown-container"
            testID="calendar-dropdown-container"
            style={isPortalUsageEnabled ? portalStyle : dropDownStyle}
          >
            <DatePickerMenu
              isSingleSelection={tripType === TRIP_TYPES.ONE_WAY}
              minimumDate={calendarMinDate}
              maximumDate={calendarMaxDate}
              minimumDisplayDate={defaultMinDate}
              maximumDisplayDate={maximumDisplayDate}
              selectedDate={draftDates}
              onChange={handleDateChange}
              primaryButtonLabel={t('hotelSearchPage.calendar.confirm')}
              secondaryButtonLabel={t('hotelSearchPage.calendar.reset')}
              primaryButtonOnPress={handlePrimaryButtonPress}
              secondaryButtonOnPress={onResetPress}
              showDaysIndicator
              priceValues={resolvedPriceValues || []}
              unavailablePricePlaceHolder={unavailablePricePlaceHolder}
              onViewableItems={onViewableItems}
              activeField={
                activeField === ACTIVE_FIELDS.RETURN &&
                draftDates[0] &&
                draftDates[1]
                  ? ACTIVE_FIELDS.DEPART
                  : activeField
              }
              onActiveFieldChange={
                draftDates[0] && draftDates[1] ? () => {} : setActiveField
              }
              calendarScrollIndex={calendarScrollIndex}
            />
          </View>
        </PortalWrapperView>
      ) : (
        <DatePickerBottomSheetComponent
          ref={datePickerRef}
          datePickerProps={datePickerProps}
          bottomSheetViewProps={bottomSheetViewProps}
          stickyBottomBarViewProps={stickyBottomBarViewProps}
        />
      )}

      <Modal
        transparent
        visible={!!internalError}
        animationType="fade"
        pointerEvents="box-none"
      >
        <View style={styles.snackbarModalContainer} pointerEvents="box-none">
          <SnackbarView
            message={internalError}
            duration={SnackbarDuration.SHORT}
            onDismiss={handleDismissError}
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  inputRow: {
    flexDirection: 'row',
    gap: pd.ref.space.spacing30.px,
    paddingVertical: {
      md: 0,
    },
  },
  flex1: { flex: 1, width: '100%' },
  snackbarModalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    pointerEvents: 'box-none',
    paddingBottom: pd.ref.space.spacing90.px + pd.ref.space.spacing40.px,
  },
});

export default CalendarWidgetView;
